import { useEffect, useState } from 'react'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    Checkbox,
    Menu,
    MenuItem,
    IconButton,
    Switch,
    FormGroup,
    FormControlLabel
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchBar from 'components/common/SearchBar'
import SortingIcon from 'components/SortingIcon'
import PaginationComponent from 'components/PaginationComponent'
import { useDeleteJourneysMutation, useJourneysList } from './queries/useJourneysQuery'
import EmptyJourneys from './components/EmptyJourneys'
import CircularProgress from '@mui/material/CircularProgress'
import { useProject } from 'context/ProjectContext'
import { useDebounce } from 'utils/debounce'
import clsx from 'clsx'
import styles from './styles.module.scss'
import TestJourneyItem from './components/TestJourneyItem'
import DeleteConfirmationModal from 'components/common/DeleteConfirmationModal'
import CreateJourneyModal from './components/CreateJourneyModal'
import ChromeExtensionModal from './components/ChromeExtensionModal'
import { socketEventEmitter } from 'socket'
import { useQueryClient } from '@tanstack/react-query'
import journeysQueryKeys from './queries/keys'
import { DeleteOutlineOutlined } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { journeyQueryKeys } from 'pages/SingleJourney/queries/keys'
import YoutubeVideoDialog from 'components/common/YoutubeVideoDialog'
import { TEST_JOURNEY_TUTORIAL_VIDEO_ID } from 'constants/tutorialVideos'

const TestJourneysContent = () => {
    const { selectedProject } = useProject()
    const queryClient = useQueryClient()
    const location = useLocation()
    const navigate = useNavigate()
    const [checkedJourneys, setCheckedJourneys] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 200);
    const [anchorEl, setAnchorEl] = useState(null)
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false)
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc', archived: false })
    const [page, setPage] = useState(1)
    const openAnchorEl = Boolean(anchorEl)
    const journeysPerPage = 10
    const { data: journeysResponse, isLoading: isJourneyListLoading, isError } = useJourneysList({ projectId: selectedProject?.id, page, limit: journeysPerPage, searchQuery: debouncedSearch, sortConfig });

    const testJourneysList = journeysResponse?.journeys || [];

    const deleteCleanUp = () => {
        setCheckedJourneys([])
        setIsHeaderCheckboxChecked(false)
        setIdToDelete(null)
        setOpenDeleteModal(false)
    }

    const [idToDelete, setIdToDelete] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const { mutateAsync: deleteJourneysAsync, isPending: isJourneyDeleteProcessing } = useDeleteJourneysMutation({
        projectId: selectedProject?.id,
        idsToDelete: idToDelete ? idToDelete : checkedJourneys,
        onSuccess: deleteCleanUp,
        onError: deleteCleanUp
    })

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openExtensionModal, setOpenExtensionModal] = useState(false);
    const [isTutorialOpen, setIsTutorialOpen] = useState(false);

    const handleExtensionClose = ({ shouldOpenCreateModal }) => {
        setOpenExtensionModal(false);
        if (shouldOpenCreateModal) {
            setOpenCreateModal(true);
        }
    }

    const handleCreate = ({ isSuccess, createdJourney }) => {
        setOpenCreateModal(false);
        if (isSuccess && createdJourney) {
            // Prepare target URL with journeyId
            let targetUrl = createdJourney.start_url;
            try {
                const url = new URL(createdJourney.start_url);
                url.searchParams.set('journeyId', createdJourney.id);
                targetUrl = url.toString();
            } catch (error) {
                const base = createdJourney.start_url || '';
                const separator = base.includes('?') ? '&' : '?';
                targetUrl = `${base}${separator}journeyId=${createdJourney.id}`;
            }

            const payload = {
                source: 'treegress',
                journeyId: createdJourney.id,
                journeyName: createdJourney.name,
                startUrl: createdJourney.start_url,
                issuedAt: Date.now(),
            };

            // Try to use Chrome Extension API to open a new window (most reliable)
            // This requires the extension to be installed and listening for messages
            const tryExtensionWindowOpen = () => {
                return new Promise((resolve, reject) => {
                    try {
                        const customEvent = new CustomEvent('treegress:open-recording-window', {
                            detail: {
                                url: targetUrl,
                                payload: payload
                            }
                        });
                        
                        // Listen for response
                        const handleResponse = (event) => {
                            window.removeEventListener('treegress:window-opened', handleResponse);
                            
                            if (event.detail?.success) {
                                resolve(true);
                            } else {
                                reject(new Error('Extension failed to open window'));
                            }
                        };
                        
                        window.addEventListener('treegress:window-opened', handleResponse);
                        
                        // Dispatch the event
                        window.dispatchEvent(customEvent);
                        
                        // Timeout after 2 seconds
                        setTimeout(() => {
                            window.removeEventListener('treegress:window-opened', handleResponse);
                            reject(new Error('Extension timeout - no response'));
                        }, 2000);
                        
                    } catch (error) {
                        reject(error);
                    }
                });
            };

            // Fallback to standard window.open
            const fallbackWindowOpen = () => {
                try {
                    // Calculate window dimensions
                    const width = Math.min(1920, window.screen.availWidth);
                    const height = Math.min(1080, window.screen.availHeight);
                    const left = Math.max(0, (window.screen.width - width) / 2);
                    const top = Math.max(0, (window.screen.height - height) / 2);

                    const windowFeatures = [
                        `popup=yes`,
                        `width=${width}`,
                        `height=${height}`,
                        `left=${left}`,
                        `top=${top}`,
                        `toolbar=no`,
                        `menubar=no`,
                        `location=yes`,
                        `resizable=yes`,
                        `scrollbars=yes`,
                        `status=no`
                    ].join(',');

                    const newWindow = window.open(targetUrl, 'treegressRecording', windowFeatures);

                    if (newWindow) {
                        newWindow.focus();
                        setTimeout(() => {
                            try {
                                newWindow.name = JSON.stringify(payload);
                            } catch (error) {
                                // Silently ignore
                            }
                        }, 100);
                    } else {
                        window.open(targetUrl, '_blank');
                    }
                } catch (error) {
                    window.open(targetUrl, '_blank');
                }
            };

            // Try extension first, fallback to standard method
            tryExtensionWindowOpen().catch(() => {
                fallbackWindowOpen();
            });
        }
    }

    const handlePageChange = (_, value) => {
        setPage(value)
    }

    const handleSearch = (_, newValue) => {
        setSearchQuery(newValue?.name?.toLowerCase() || newValue || '')
        setPage(1)
    }

    const handleClickCheckAllDropdown = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseCheckAllDropdown = () => {
        setAnchorEl(null)
    }

    const handleCheckAllCurrentPage = () => {
        const currentPageJourneysIds = testJourneysList.map(item => item.id)
        setCheckedJourneys(prev => [
            ...new Set([...prev, ...currentPageJourneysIds])
        ])
        setIsHeaderCheckboxChecked(true)
        handleCloseCheckAllDropdown()
    }

    const handleUncheckAllCurrentPage = () => {
        const currentPageJourneyIds = testJourneysList.map(item => item.id)
        setCheckedJourneys(prev =>
            prev.filter(id => !currentPageJourneyIds.includes(id))
        )
        setIsHeaderCheckboxChecked(false)
        handleCloseCheckAllDropdown()
    }

    const handleCheckboxChange = (id) => {
        setCheckedJourneys(prev => {
            if (prev.includes(id)) {
                setIsHeaderCheckboxChecked(false)
                return prev.filter(journeyId => journeyId !== id)
            } else {
                return [...prev, id]
            }
        })
    }

    const handleSortChange = (key) => {
        setPage(1)
        setSortConfig((prevConfig) => ({
            ...prevConfig,
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
        }))
    }

    const handleSingleDelete = (id) => {
        setIdToDelete(id)
        setOpenDeleteModal(true);
    }

    const handleAddJourney = () => setOpenExtensionModal(true);

    // Check if we came from the extension setup page and should open create modal
    useEffect(() => {
        if (location.state?.openCreateModal) {
            setOpenCreateModal(true);
            // Clear the state to prevent reopening on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    useEffect(() => {
        console.log("use effect.")
        const handleJourneyUpdated = (payload) => {
            console.log('Received journey:updated event with payload:', payload);
            const journeyProjectId = payload?.project_id || payload?.projectId;
            if (selectedProject?.id && journeyProjectId && journeyProjectId !== selectedProject.id) {
                return;
            }
            queryClient.invalidateQueries({
                predicate: ({ queryKey }) =>
                    Array.isArray(queryKey) &&
                    queryKey[0] === journeysQueryKeys.journeyList,
            });
        };

        socketEventEmitter.on('journey:updated', handleJourneyUpdated);
        return () => {
            socketEventEmitter.off('journey:updated', handleJourneyUpdated);
        };
    }, [queryClient, selectedProject?.id]);

    useEffect(() => {
        if(!selectedProject?.id || !testJourneysList) return;
        const handleTaskUpdate = (payload) => {
            if (!payload) return;
            const journeyId = payload.journeyId;
            if (journeyId && testJourneysList.some(item => item.id === journeyId)) {
                queryClient.invalidateQueries({
                    queryKey: [journeysQueryKeys.journeyList, selectedProject?.id],
                });
            }
        }
        socketEventEmitter.on('taskCompleted', handleTaskUpdate);
        socketEventEmitter.on('taskActivated', handleTaskUpdate);
        socketEventEmitter.on('taskFailed', handleTaskUpdate);
        return () => {
            socketEventEmitter.off('taskCompleted', handleTaskUpdate);
            socketEventEmitter.off('taskFailed', handleTaskUpdate);
            socketEventEmitter.off('taskActivated', handleTaskUpdate);
        };
    
    }, [selectedProject?.id, queryClient, testJourneysList, journeyQueryKeys])

    const handleArchived = (value) => {
        setSortConfig(prev => ({...prev, archived: value}));
    }

    const renderTableBody = () => {
        if(testJourneysList.length === 0) {
            if(!sortConfig.archived) {
                return (
                    <TableRow>
                        <TableCell align='center' colSpan={5}>
                            <span>You don’t have any test journeys yet.</span>
                        </TableCell>
                    </TableRow>
                );
            }
            return (
                <TableRow>
                    <TableCell align='center' colSpan={5}>
                        <span>You don’t have any archived test journeys yet.</span>
                    </TableCell>
                </TableRow>
            );
        }
        return testJourneysList.map((item) => (
            <TestJourneyItem
                key={item.id}
                journey={item}
                checkedIds={checkedJourneys}
                handleCheckboxChange={handleCheckboxChange}
                handleOpenDeleteDialog={handleSingleDelete}
            />
        ));
    }

    if (isJourneyListLoading) return (
        <Box className={styles.loaderWrapper}>
            <CircularProgress />
        </Box>
    );
    if (isError || !journeysResponse?.total) {
        return (
            <>
                <EmptyJourneys handleAddJourney={handleAddJourney} />
                <ChromeExtensionModal
                    open={openExtensionModal}
                    onClose={handleExtensionClose}
                />
                <CreateJourneyModal
                    open={openCreateModal}
                    onClose={handleCreate}
                />
                <YoutubeVideoDialog
                    open={isTutorialOpen}
                    onClose={() => setIsTutorialOpen(false)}
                    videoId={TEST_JOURNEY_TUTORIAL_VIDEO_ID}
                    title="How to add your first Test Journey"
                />
            </>
        );
    }

    return (
        <>
            <Box className={styles.journeysTopSection}>
                <Typography className={styles.journeysTitle}>Test journeys</Typography>
                <Button
                    className={styles.journeysActionButton}
                    variant="contained"
                    color="secondary"
                    onClick={handleAddJourney}
                >
                    Add New Test Journey
                </Button>
            </Box>
            <Box className={styles.journeysMiddleSection}>
                <Box>
                    <Button
                        className={styles.journeysTutorialButton}
                        variant="text"
                        color="secondary"
                        onClick={() => setIsTutorialOpen(true)}
                    >
                        Watch a short video tutorial on how to add your first Test Journey.
                    </Button>
                    {checkedJourneys.length > 0 && (
                        <Button
                            className={styles.journeysDelete}
                            variant="outlined"
                            color="error"
                            onClick={() => setOpenDeleteModal(true)}
                        >
                           <DeleteOutlineOutlined className={styles.deleteIcon}/> 
                            Delete
                        </Button>
                    )}
                </Box>
                <Box className={styles.rightContainer}>
                    <SearchBar
                        list={testJourneysList}
                        handleSearch={handleSearch}
                    />
                    <FormGroup className={styles.archiveSwitch}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    value={sortConfig.archived}
                                    onChange={(e) => handleArchived(e.target.checked)}
                                />
                            } 
                            label="Show Archived"  
                        />
                    </FormGroup>
                </Box>
            </Box>
            <TableContainer className={styles.journeysTableContainer} component={Paper}>
                <Table 
                    className={styles.journeysTable} 
                    stickyHeader
                >
                    <TableHead className={styles.journeysTableHeader}>
                        <TableRow>
                            <TableCell className={styles.journeysTableHeaderCheckboxCell}>
                                <Checkbox
                                    className={styles.journeysTableHeaderCheckbox}
                                    checked={isHeaderCheckboxChecked}
                                    onChange={(e) => {
                                        const allChecked = e.target.checked
                                        if (!allChecked) {

                                            return handleUncheckAllCurrentPage()
                                        }
                                        handleCheckAllCurrentPage()
                                    }}
                                />
                                <IconButton
                                    onClick={handleClickCheckAllDropdown}
                                    size="small"
                                    sx={{ p: 0 }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={openAnchorEl}
                                    onClose={handleCloseCheckAllDropdown}
                                >
                                    <MenuItem onClick={handleCheckAllCurrentPage} sx={{ minWidth: '100px' }}>All</MenuItem>
                                    <MenuItem onClick={handleUncheckAllCurrentPage}>None</MenuItem>
                                </Menu>
                            </TableCell>
                            <TableCell className={clsx(styles.journeysTableHeaderCell, styles.journeysName)} onClick={() => handleSortChange('name')}>
                                <Box className={styles.journeysTableHeaderCellContent}>
                                    <SortingIcon sortConfig={sortConfig} currentKey={'name'} />
                                    Test journey name
                                </Box>
                            </TableCell>
                            <TableCell className={styles.journeysTableHeaderCell} onClick={() => handleSortChange('created_at')}>
                                <Box className={styles.journeysTableHeaderCellContent}>
                                    <SortingIcon sortConfig={sortConfig} currentKey={'created_at'} />
                                    Creation Date
                                </Box>
                            </TableCell>
                            <TableCell className={styles.journeysTableHeaderCell} onClick={() => handleSortChange('updated_at')}>
                                <Box className={styles.journeysTableHeaderCellContent}>
                                    <SortingIcon sortConfig={sortConfig} currentKey={'updated_at'} />
                                    Last Updated Date
                                </Box>
                            </TableCell>
                            <TableCell className={styles.journeysTableHeaderCell} onClick={() => handleSortChange('status')}>
                                <Box className={styles.journeysTableHeaderCellContent}>
                                    <SortingIcon sortConfig={sortConfig} currentKey={'status'} />
                                    Status
                                </Box>
                            </TableCell>
                            <TableCell className={clsx(styles.journeysTableHeaderCellContent, styles.journeysActions)}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableBody()}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaginationComponent
                page={page}
                totalItems={journeysResponse?.count || 0}
                itemsPerPage={journeysPerPage}
                count={Math.ceil((journeysResponse?.count || 0) / journeysPerPage)}
                handlePageChange={handlePageChange}
            />
            <DeleteConfirmationModal
                deleteOpen={openDeleteModal}
                handleClose={deleteCleanUp}
                idsToDelete={idToDelete ? [idToDelete] : checkedJourneys}
                isLoading={isJourneyDeleteProcessing}
                essanceName={'journey'}
                handleDelete={deleteJourneysAsync}
            />
            <ChromeExtensionModal
                open={openExtensionModal}
                onClose={handleExtensionClose}
            />
            <CreateJourneyModal
                open={openCreateModal}
                onClose={handleCreate}
            />
            <YoutubeVideoDialog
                open={isTutorialOpen}
                onClose={() => setIsTutorialOpen(false)}
                videoId={TEST_JOURNEY_TUTORIAL_VIDEO_ID}
                title="How to add your first Test Journey"
            />
        </>
    )
}

export default TestJourneysContent
